import { supabase } from './supabase';
import { useAuth } from '../contexts/AuthContext';

export interface CollabInfluencer {
  id?: string;
  fullName: string;
  deliverables: any[];
  rate: string;
  creator_invoice_sent: boolean;
  paidPercentage?: number;
}

export interface Collab {
  id?: string;
  agency_id: string;
  agency_name?: string;
  created_by?: string;
  name: string;
  date?: string;
  brand: string;
  influencers: CollabInfluencer[];
  influencer_ids?: string[];
  quote_sent: boolean;
  quote_number?: string;
  contract_signed: boolean;
  signed_date?: string;
  manager: string;
  post_progress: '0' | '30' | '50' | '100';
  billing_status: string;
  invoice_number?: string;
  payment_received: boolean;
  invoice_sent?: boolean;
  type: 'collaboration_entrante' | 'collaboration_sortante' | 'casting_interne' | 'casting_externe';
  status?: 'pending' | 'in_progress' | 'completed';
  public_token?: string;
  public_link_enabled?: boolean;
  budget?: number;
  link_token?: string;
  link_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface Client {
  id?: string;
  agency_id: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  industry?: string;
  status: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface TimelineItem {
  id?: string;
  collab_id: string;
  influencer_id?: string;
  influencer_name?: string;
  step_name: string;
  deadline?: string;
  status: 'todo' | 'in_progress' | 'done' | 'late';
  order: number;
  created_at?: string;
  updated_at?: string;
}

export interface CollabDocument {
  id?: string;
  collab_id: string;
  influencer_id?: string;
  influencer_name?: string;
  name: string;
  type: 'brief' | 'contract' | 'other';
  file_url: string;
  file_name: string;
  file_size: number;
  visible_for_brand: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CollabStats {
  id?: string;
  collab_id: string;
  influencer_id?: string;
  influencer_name?: string;
  post_thumb?: string;
  post_url?: string;
  views: number;
  likes: number;
  engagement_rate: number;
  creator: string;
  created_at?: string;
  updated_at?: string;
}

export interface Todo {
  id?: string;
  agency_id: string;
  user_id: string;
  assigned_to?: string;
  title: string;
  description?: string;
  status: 'pending' | 'in_progress' | 'completed';
  priority: 'low' | 'medium' | 'high';
  due_date?: string;
  completed_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Resource {
  id?: string;
  agency_id: string;
  name: string;
  type: 'brief' | 'contract' | 'email' | 'training' | 'video' | 'other';
  description?: string;
  url?: string;
  file_url?: string;
  file_name?: string;
  file_type?: string;
  file_size?: number;
  created_at?: string;
  updated_at?: string;
}

export interface CastingBrief {
  id?: string;
  agency_id: string;
  brand: string;
  name: string;
  description: string;
  requirements: string[];
  budget: string;
  start_date?: string;
  end_date?: string;
  status: 'draft' | 'active' | 'closed';
  selected_influencers: string[];
  proposed_influencers: any[];
  public_token?: string;
  public_link_active?: boolean;
  deadline?: string;
  activity_log?: any[];
  created_at?: string;
  updated_at?: string;
}

export async function getDocuments<T>(
  tableName: string,
  agencyId?: string
): Promise<T[]> {
  try {
    let query = supabase.from(tableName).select('*');

    if (agencyId) {
      query = query.eq('agency_id', agencyId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return (data || []) as T[];
  } catch (error) {
    console.error(`Error getting documents from ${tableName}:`, error);
    return [];
  }
}

export async function getDocument<T>(
  tableName: string,
  id: string
): Promise<T | null> {
  try {
    const { data, error } = await supabase
      .from(tableName)
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;

    return data as T | null;
  } catch (error) {
    console.error(`Error getting document from ${tableName}:`, error);
    return null;
  }
}

function convertToSnakeCase(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(convertToSnakeCase);
  if (obj instanceof Date) return obj.toISOString();
  if (typeof obj !== 'object') return obj;

  const snakeCaseObj: any = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      // Récursivement convertir les valeurs qui sont des objets
      snakeCaseObj[snakeKey] = convertToSnakeCase(obj[key]);
    }
  }
  return snakeCaseObj;
}

export async function createDocument<T>(
  tableName: string,
  data: Partial<T>
): Promise<T | null> {
  try {
    // Remove id from data if present to let database generate it
    const { id, ...dataWithoutId } = data as any;

    // Convert camelCase to snake_case for tables that use snake_case
    const needsSnakeCase = ['collabs', 'todos', 'casting_briefs', 'timeline_items', 'collab_documents'];
    const dataToInsert = needsSnakeCase.includes(tableName) ? convertToSnakeCase(dataWithoutId) : dataWithoutId;

    const { data: newDoc, error } = await supabase
      .from(tableName)
      .insert([dataToInsert])
      .select()
      .single();

    if (error) {
      console.error(`Supabase error creating document in ${tableName}:`, error);
      throw error;
    }

    return newDoc as T;
  } catch (error) {
    console.error(`Error creating document in ${tableName}:`, error);
    throw error;
  }
}

export async function updateDocument<T>(
  tableName: string,
  id: string,
  data: Partial<T>
): Promise<T | null> {
  try {
    // Convert camelCase to snake_case for tables that use snake_case
    const needsSnakeCase = ['collabs', 'todos', 'casting_briefs', 'timeline_items', 'collab_documents'];
    const dataToUpdate = needsSnakeCase.includes(tableName) ? convertToSnakeCase(data) : data;

    const { data: updatedDoc, error } = await supabase
      .from(tableName)
      .update(dataToUpdate)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return updatedDoc as T;
  } catch (error) {
    console.error(`Error updating document in ${tableName}:`, error);
    throw error;
  }
}

export async function deleteDocument(
  tableName: string,
  id: string
): Promise<{ success: boolean; message?: string }> {
  try {
    const { error } = await supabase
      .from(tableName)
      .delete()
      .eq('id', id);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error(`Error deleting document from ${tableName}:`, error);
    return { success: false, message: error.message };
  }
}
